import axios from 'axios';

const ssoAPI = axios.create({
  baseURL: process.env.REACT_APP_SSO_API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * SSO Login - Authenticate user credentials using Kit19 Auth API
 */
export const ssoLogin = async (username, password, appName, appConfig = null) => {
  try {
    // Get IP address (in production, you'd get this from server)
    const ipAddress = await getClientIP();
    
    // Extract configuration values
    const loginMode = appConfig?.loginMode || 'desktopapplogin';
    const userIdGuid = appConfig?.UserIdGuid || null;
    const refDomain = appConfig?.refDomain || window.location.hostname;
    const deviceId = getDeviceId();
    
    // Build payload matching Kit19 Auth API format
    const payload = {
      Token: 0,
      Message: null,
      LoggedUserId: null,
      MAC_Address: ipAddress,
      IP_Address: ipAddress,
      Details: {
        LoginName: username,
        Password: password,
        Url: refDomain,
        DeviceId: deviceId,
        LoginMode: loginMode,
        AppName: appName || 'Kit19',
        DeviceType: 'Desktop',
        UserIdGuid: userIdGuid
      },
      BroadcastName: null
    };

    const response = await ssoAPI.post('Authorize/Login', payload);
    
    // Transform response to match expected format
    // Response structure: { status, message, details: { userInfo, tokenInfo, domainInfo, meta } }
    if (response.data && response.data.status === 1) {
      const userInfo = response.data.details?.userInfo;
      const tokenInfo = response.data.details?.tokenInfo;
      const domainInfo = response.data.details?.domainInfo;
      const meta = response.data.details?.meta;
      
      return {
        success: true,
        token: tokenInfo?.accessToken,
        userData: {
          userId: userInfo?.userId,
          User_ID: userInfo?.userId,
          parentId: userInfo?.parentId,
          ParentID: userInfo?.parentId,
          firstName: meta?.userDetail?.fName || meta?.teamChat?.FName || '',
          FName: meta?.userDetail?.fName || meta?.teamChat?.FName || '',
          lastName: meta?.userDetail?.lName || meta?.teamChat?.LName || '',
          LName: meta?.userDetail?.lName || meta?.teamChat?.LName || '',
          email: meta?.userDetail?.email || meta?.teamChat?.EMail || '',
          EMail: meta?.userDetail?.email || meta?.teamChat?.EMail || '',
          mobile: meta?.userDetail?.mobile || meta?.teamChat?.Mobile || '',
          Mobile: meta?.userDetail?.mobile || meta?.teamChat?.Mobile || '',
          profilePicture: meta?.teamChat?.ProfilePicturePath || '',
          ProfilePicturePath: meta?.teamChat?.ProfilePicturePath || '',
          domain: domainInfo?.domainName || '',
          Domain: domainInfo?.domainName || '',
          logo: domainInfo?.companyLogo || '',
          Logo: domainInfo?.companyLogo || '',
          displayName: userInfo?.displayName || '',
          DisplayName: userInfo?.displayName || '',
          username: userInfo?.userLogin || username,
          LoginName: userInfo?.userLogin || username,
          companyName: meta?.userDetail?.companyName || domainInfo?.companyName || '',
          timezone: meta?.userDetail?.timezone || '',
          countryCode: meta?.userDetail?.countryCode || '',
          // Additional fields for compatibility
          tpUserId: userInfo?.tpUserId,
          appName: userInfo?.appName,
          loginMode: userInfo?.loginMode,
          clientIp: userInfo?.clientIp
        }
      };
    } else {
      return {
        success: false,
        message: response.data?.message || 'Login failed',
        details: response.data?.details || null
      };
    }
  } catch (error) {
    console.error('SSO Login error:', error);
    const friendlyMessage = error.response
      ? error.response.data?.message || 'Authentication failed'
      : 'Unable to reach the login server. Please check your connection and try again.';
    return {
      success: false,
      message: friendlyMessage
    };
  }
};

/**
 * Validate SSO Token with auth.kit19.com
 */
export const validateToken = async (token) => {
  try {
    const response = await ssoAPI.post('Authorize/ValidateToken', 
      { accessToken: token },
      {
        headers: {
          'Auth-Key': token,
          'Authorization': `Bearer ${token}`
        }
      }
    );
    
    // The auth API answers a live session with:
    //   { status: 1, message, details: { userInfo, tokenInfo, domainInfo, meta } }
    // There is no top-level `isValid` flag in that payload, so requiring one made every
    // valid token look invalid and forced the user to re-enter credentials even though
    // an SSO session already existed. Treat "outer status 1 + user payload returned" as
    // valid, and only honour `isValid` / `details.status` when the API actually sends them.
    const data = response.data || {};
    const details = data.details || null;

    const outerOk = Number(data.status) === 1;
    const explicitlyInvalid =
      data.isValid === false ||
      (details && details.status !== undefined && Number(details.status) !== 1);

    if (outerOk && details && !explicitlyInvalid) {
      return {
        valid: true,
        userData: details
      };
    }

    return {
      valid: false,
      message: data.message || details?.message || 'Token validation failed'
    };
  } catch (error) {
    console.error('Token validation error:', error);
    const httpStatus = error.response?.status;
    // No response at all, or the auth server itself blew up: we cannot tell whether the
    // session is still good, so flag it and let the caller fall back to the token's own expiry.
    // Only an explicit 401/403 (or any other 4xx) really means "this token is not accepted".
    const networkError = !error.response || httpStatus >= 500;
    return {
      valid: false,
      networkError,
      message: error.response?.data?.message || error.message || 'Token validation failed'
    };
  }
};

/**
 * Get user profile by token
 */
export const getUserProfile = async (token) => {
  try {
    const response = await ssoAPI.get('Authorize/Profile', {
      headers: {
        'Auth-Key': token,
        'Authorization': `Bearer ${token}`
      }
    });
    return response.data;
  } catch (error) {
    console.error('Get profile error:', error);
    throw error.response?.data || error;
  }
};

/**
 * SSO Logout
 */
export const ssoLogout = async (token) => {
  try {
    const response = await ssoAPI.post('Authorize/Logout', {}, {
      headers: {
        'Auth-Key': token,
        'Authorization': `Bearer ${token}`
      }
    });
    return response.data;
  } catch (error) {
    console.error('Logout error:', error);
    throw error.response?.data || error;
  }
};

export const ssoImpersonate = async (token, payload) => {
  try {    
    const ipAddress = await getClientIP();    
    const _payload = {
      Token: 0,
      Message: null,
      LoggedUserId: null,
      MAC_Address: ipAddress,
      IP_Address: ipAddress,
      Details: payload,
      BroadcastName: null
    }

    console.log('Impersonation payload:', JSON.stringify(_payload, null, 2));
    return ssoAPI.post('Authorize/Impersonate', _payload, {
      headers: {
        'Auth-Key': token,
        'Authorization': `Bearer ${token}`
      }
    });

  } catch (error) {
    console.error('Impersonation error:', error);
    throw error.response?.data || error;
  }
};

/**
 * Get or create device ID
 */
function getDeviceId() {
  let deviceId = localStorage.getItem('kit19_device_id');
  if (!deviceId) {
    deviceId = 'web-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9);
    localStorage.setItem('kit19_device_id', deviceId);
  }
  return deviceId;
}

/**
 * Get client IP address
 */
async function getClientIP() {
  try {
    const response = await fetch('https://api.ipify.org?format=json');
    const data = await response.json();
    return data.ip;
  } catch (error) {
    console.warn('Could not fetch IP address:', error);
    return getDeviceId(); // Fallback to device ID
  }
}

/**
 * Get Domain Information and Configuration
 */
export const getDomainInfo = async (url) => {
  try {
    // Endpoint: GET Authorize/DomainInfo?url=domain.com
    // Pages (Login/Impersonate) wait on this lookup, so don't let a hung API block them forever
    const response = await ssoAPI.get('Authorize/DomainInfo', {
      params: { url },
      timeout: 10000
    });

    // Expected response: { parentId, domainName, domainType, loginPage, backgroundImage, companyLogo, etc. }
    if (response.data) {
      return {
        success: true,
        data: response.data
      };
    }

    return {
      success: false,
      message: 'Could not fetch domain information'
    };
  } catch (error) {
    console.error('Domain info error:', error);
    return {
      success: false,
      message: error.response?.data?.message || 'Error fetching domain information',
      data: null
    };
  }
};

/**
 * Check Email Availability
 */
export const checkEmailAvailability = async (email) => {
  try {
    // New endpoint: GET Authorize/EmailCheck?email=...
    const response = await ssoAPI.get('Authorize/EmailCheck', {
      params: { email }
    });

    // Expected response example:
    // { data: [ { msg: "EmailID Already Exists", id: -1 } ] }
    if (response.data && Array.isArray(response.data.data) && response.data.data.length > 0) {
      const item = response.data.data[0];
      // id === -1 means already exists (not available)
      const available = item.id !== -1;
      return {
        available,
        message: item.msg || ''
      };
    }

    return {
      available: false,
      message: 'Could not check email availability'
    };
  } catch (error) {
    console.error('Email check error:', error);
    return {
      available: false,
      message: error.response?.data?.message || 'Error checking email availability'
    };
  }
};

/**
 * Get Country by Country Code
 */
export const getCountryByCode = async (countryCode) => {
  try {
    // New endpoint: GET Authorize/CountryCodeWithTimeZone?countryCode=%2B91
    const response = await ssoAPI.get('Authorize/CountryCodeWithTimeZone', {
      params: { countryCode: countryCode.trim() }
    });

    // Expected response: { data: [ { country_ID, country_Name, countryCode } ] }
    const items = response.data?.data;
    if (Array.isArray(items) && items.length > 0) {
      // Normalize to array of { Value, Text, code }
      return items.map(i => ({
        Value: i.country_ID ?? i.countryId ?? i.Value ?? i.value ?? '',
        Text: i.country_Name ?? i.countryName ?? i.Text ?? i.text ?? '',
        code: i.countryCode ?? i.country_Code ?? ''
      }));
    }

    return [];
  } catch (error) {
    console.error('Get country error:', error);
    return [];
  }
};

/**
 * Get Timezone by Country ID
 */
export const getTimezoneByCountry = async (countryId) => {
  try {
    // New endpoint: GET Authorize/GetTimeZoneByCountryId?countryId=101
    const response = await ssoAPI.get('Authorize/GetTimeZoneByCountryId', {
      params: { countryId }
    });

    // Expected response: { data: [ { timeZone, timeZoneDisplay } ] }
    const items = response.data?.data;
    if (Array.isArray(items) && items.length > 0) {
      // Normalize to array of { Value, Text }
      return items.map(i => ({
        Value: i.timeZone ?? i.TimeZone ?? i.value ?? i.Value ?? '',
        Text: i.timeZoneDisplay ?? i.timeZone ?? i.TimeZoneDisplay ?? i.Text ?? ''
      }));
    }

    return [];
  } catch (error) {
    console.error('Get timezone error:', error);
    return [];
  }
};

/**
 * Register New User
 */
export const registerUser = async (formData, refUrl = '') => {
  try {
    // RefUrl should be just the scheme + host (e.g. https://whatsapp.kit19.com),
    // not the full redirect_uri path (e.g. https://whatsapp.kit19.com/login)
    let refOrigin = '';
    if (refUrl) {
      try {
        refOrigin = new URL(refUrl).origin;
      } catch (e) {
        refOrigin = refUrl;
      }
    } else if (typeof window !== 'undefined') {
      refOrigin = window.location.origin;
    }

    // New register endpoint: POST Authorize/Register
    const payload = {
      UserName: formData.userName,
      FirstName: formData.firstName,
      LastName: formData.lastName || '',
      Email: formData.email,
      Mobile: formData.mobile,
      CountryCode: formData.countryCode,
      CountryId: formData.countryId,
      TimezoneId: formData.timezoneId,
      CompanyName: formData.companyName,
      RefUrl: refOrigin
    };

    const response = await ssoAPI.post('Authorize/Register', payload);

    // Handle new API response format:
    // Success: { data: [{ ResultMessage: "You have registered Successfully" }], message: "...", isSuccess: true }
    // Failure: { data: [], message: "Email already registered.", isSuccess: false }
    if (response.data) {
      const { isSuccess, message, data } = response.data;
      
      // Check isSuccess flag first
      if (isSuccess === true) {
        // Extract ResultMessage from data array if available
        const resultMessage = (data && data.length > 0 && data[0].ResultMessage) 
          ? data[0].ResultMessage 
          : message || 'Registration successful';
        
        return {
          success: true,
          message: resultMessage,
          data: data || []
        };
      } else {
        // Registration failed (validation error or email already exists)
        return {
          success: false,
          message: message || 'Registration failed',
          data: data || []
        };
      }
    }
    
    // Fallback for unexpected response format
    return {
      success: false,
      message: 'Unexpected response from server'
    };
  } catch (error) {
    console.error('Registration error:', error);
    
    // Handle error response from server
    const errorData = error.response?.data;
    if (errorData) {
      return {
        success: false,
        message: errorData.message || errorData.error || 'Registration failed',
        data: errorData.data || []
      };
    }
    
    return {
      success: false,
      message: error.message || 'Registration failed. Please try again.'
    };
  }
};

export default ssoAPI;
